import { Loader2 } from 'lucide-react';

/**
 * Button - Reusable button component adapted to theme
 *
 * @param {Object} props
 * @param {'primary'|'secondary'|'danger'|'ghost'|'outline'} props.variant - Button style variant
 * @param {'sm'|'md'|'lg'} props.size - Button size
 * @param {boolean} props.loading - Show loading spinner
 * @param {boolean} props.disabled - Disable button
 * @param {boolean} props.fullWidth - Full width button
 * @param {React.ReactNode} props.icon - Icon to display before text
 * @param {React.ReactNode} props.iconRight - Icon to display after text
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Click handler
 * @param {'button'|'submit'|'reset'} props.type - Button type
 */
const Button = ({
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    icon: Icon,
    iconRight: IconRight,
    className = '',
    children,
    onClick,
    type = 'button',
    ...props
}) => {
    // Size classes
    // min-h-11 (44px) sous 640px : cible tactile minimale sur mobile. Neutralisé
    // dès sm: pour conserver la densité des barres d'actions en desktop.
    // lg fait déjà 48px, il n'a pas besoin de plancher.
    const sizes = {
        sm: 'min-h-11 sm:min-h-0 px-3 py-1.5 text-sm gap-1.5',
        md: 'min-h-11 sm:min-h-0 px-4 py-2 text-sm gap-2',
        lg: 'px-6 py-3 text-base gap-2'
    };

    // Icon sizes
    const iconSizes = {
        sm: 'w-3.5 h-3.5',
        md: 'w-4 h-4',
        lg: 'w-5 h-5'
    };

    // Variant classes - using theme CSS variables
    const variants = {
        primary: `
            bg-gradient-to-r from-cabaza-purple to-cabaza-pink
            text-white font-medium
            hover:opacity-90 hover:shadow-lg hover:shadow-cabaza-purple/25
            active:opacity-95
            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none
        `,
        secondary: `
            bg-gray-100 dark:bg-gray-700
            text-gray-700 dark:text-gray-200 font-medium
            hover:bg-gray-200 dark:hover:bg-gray-600
            active:bg-gray-300 dark:active:bg-gray-500
            disabled:opacity-50 disabled:cursor-not-allowed
        `,
        danger: `
            bg-red-500
            text-white font-medium
            hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/25
            active:bg-red-700
            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none
        `,
        ghost: `
            bg-transparent
            text-gray-600 dark:text-gray-300 font-medium
            hover:bg-gray-100 dark:hover:bg-gray-700
            active:bg-gray-200 dark:active:bg-gray-600
            disabled:opacity-50 disabled:cursor-not-allowed
        `,
        outline: `
            bg-transparent border-2 border-cabaza-purple
            text-cabaza-purple font-medium
            hover:bg-cabaza-purple/10
            active:bg-cabaza-purple/20
            disabled:opacity-50 disabled:cursor-not-allowed
        `
    };

    const baseClasses = `
        inline-flex items-center justify-center
        rounded-lg
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-cabaza-purple/50 focus:ring-offset-2
    `;

    const isDisabled = disabled || loading;

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={isDisabled}
            className={`
                ${baseClasses}
                ${sizes[size]}
                ${variants[variant]}
                ${fullWidth ? 'w-full' : ''}
                ${className}
            `.trim().replace(/\s+/g, ' ')}
            {...props}
        >
            {loading ? (
                <Loader2 className={`${iconSizes[size]} animate-spin`} />
            ) : Icon ? (
                <Icon className={iconSizes[size]} />
            ) : null}

            {children}

            {!loading && IconRight && (
                <IconRight className={iconSizes[size]} />
            )}
        </button>
    );
};

export default Button;
